import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft } from "lucide-react";
import { Editor } from '@tinymce/tinymce-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { EVENT_TYPES } from "@/types/notifications";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

const EmailEditor = ({ template, onSave, onPreview, hideHeader = false }) => {
  const navigate = useNavigate();
  const [emailData, setEmailData] = useState({
    id: template.id || '',
    name: template.name || '',
    subject: template.subject || '',
    content: template.content || '',
    type: template.type || 'session',
    eventType: template.event_type || template.eventType || '',
    daysOffset: template.days_offset || template.daysOffset || 0,
    isActive: template.is_active !== undefined ? template.is_active : (template.isActive !== undefined ? template.isActive : true)
  });
  const [activeTab, setActiveTab] = useState("edit");
  const { toast } = useToast();
  const subjectInputRef = useRef(null);
  const editorRef = useRef(null);
  
  const showDaysOffset = emailData.eventType === EVENT_TYPES.BEFORE_TRAINING || 
                         emailData.eventType === EVENT_TYPES.AFTER_TRAINING;

  const handleChange = (field, value) => {
    setEmailData({
      ...emailData,
      [field]: value
    });
  };

  const handleSave = () => {
    if (!emailData.name) {
      toast({
        title: "Erreur",
        description: "Veuillez donner un nom à ce modèle",
        variant: "destructive"
      });
      return;
    }

    if (!emailData.subject) {
      toast({
        title: "Erreur",
        description: "Veuillez spécifier un sujet",
        variant: "destructive"
      });
      return;
    }

    // Convertir les noms de propriétés pour l'API
    const apiData = {
      ...emailData,
      event_type: emailData.eventType,
      days_offset: emailData.daysOffset,
      is_active: emailData.isActive
    };

    // Supprimer les anciens noms de propriétés
    delete apiData.eventType;
    delete apiData.daysOffset;
    delete apiData.isActive;

    onSave(apiData);
  };

  const insertVariable = (variable) => {
    if (document.activeElement === subjectInputRef.current) {
      const input = subjectInputRef.current;
      const startPos = input.selectionStart;
      const endPos = input.selectionEnd;
      const beforeText = input.value.substring(0, startPos);
      const afterText = input.value.substring(endPos);
      
      const newValue = `${beforeText}{${variable}}${afterText}`;
      input.value = newValue;
      
      handleChange('subject', newValue);
      
      setTimeout(() => {
        input.focus();
        input.selectionStart = startPos + variable.length + 2;
        input.selectionEnd = startPos + variable.length + 2;
      }, 0);
    }
    else if (editorRef.current) {
      editorRef.current.execCommand('mceInsertContent', false, `{${variable}}`);
    }
  };

  const apprenantVariables = [
    "civilité", "nom", "prénom", "email", "téléphone", "profession", 
    "spécialité", "adresse", "code postal", "ville"
  ];

  const formationVariables = [
    "nom formation", "numéro de programme", "numéro de session", "début de session", 
    "fin de session", "progression", "date d'inscription", "date de fin d'unité clé"
  ];

  const evenementVariables = [
    "début de l'événement",
    "fin de l'événement",
    "lieu de l'événement",
    "nom du lieu présentielle",
    "adresse du lieu présentielle",
    "lien du virtuelle",
    "nom du lieu virtuelle",
    "formateur",
    "lien géolocalisation"
  ];

  const apprenantSpecificVariables = [
    "liste des formations"
  ];

  const renderVariableButtons = (variables, category) => (
    <div className="space-y-3">
      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Balises {category} :</h3>
      <div className="flex flex-wrap gap-2">
        {variables.map((variable) => (
          <Button
            key={variable}
            variant="outline"
            size="sm"
            onClick={() => insertVariable(variable)}
            className="py-1 h-auto text-xs rounded-lg border-slate-200 font-mono text-blue-600 bg-blue-50/50 hover:bg-blue-100 hover:border-blue-300 transition-colors"
          >
            {`{${variable}}`}
          </Button>
        ))}
      </div>
    </div>
  );

  const handleGoBack = () => {
    navigate('/dashboard/email-notifications');
  };

  return (
    <div>
      {!hideHeader && (
        <div className="flex items-center mb-4">
          <Button
            variant="outline"
            onClick={handleGoBack}
            className="mr-4 rounded-xl font-semibold border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <ChevronLeft className="mr-2 h-4 w-4" /> Retour
          </Button>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            {template.id === 'new' ? 'Créer un modèle' : 'Modifier le modèle'}
          </h2>
        </div>
      )}

      <ResizablePanelGroup direction="horizontal" className="min-h-[600px] rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
        <ResizablePanel defaultSize={66}>
          <div className="h-full p-6 space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="template-name">Nom du modèle</Label>
                <Input
                  id="template-name"
                  placeholder="Ex: Confirmation d'inscription"
                  value={emailData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="template-type">Type de template</Label>
                <Select 
                  value={emailData.type} 
                  onValueChange={(value) => handleChange('type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="session">Session (formation spécifique)</SelectItem>
                    <SelectItem value="apprenant">Apprenant (liste des formations)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {emailData.type === 'session' && (
                <div>
                  <Label htmlFor="event-type">Événement déclencheur (optionnel)</Label>
                  <Select 
                    value={emailData.eventType || "manual"} 
                    onValueChange={(value) => handleChange('eventType', value === "manual" ? "" : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un événement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Aucun (envoi manuel uniquement)</SelectItem>
                      <SelectItem value={EVENT_TYPES.REGISTRATION}>À l'inscription</SelectItem>
                      <SelectItem value={EVENT_TYPES.BEFORE_TRAINING}>Avant le début de la formation</SelectItem>
                      <SelectItem value={EVENT_TYPES.TRAINING_START}>Au début de la formation</SelectItem>
                      <SelectItem value={EVENT_TYPES.TRAINING_END}>À la fin de la formation</SelectItem>
                      <SelectItem value={EVENT_TYPES.AFTER_TRAINING}>Après la fin de la formation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showDaysOffset && (
                <div>
                  <Label htmlFor="days-offset">
                    {emailData.eventType === EVENT_TYPES.BEFORE_TRAINING ? 
                      "Nombre de jours avant la formation" : 
                      "Nombre de jours après la formation"}
                  </Label>
                  <Input
                    id="days-offset"
                    type="number"
                    min="1"
                    max="90"
                    value={emailData.daysOffset}
                    onChange={(e) => handleChange('daysOffset', parseInt(e.target.value) || 0)}
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="email-subject">Sujet du mail</Label>
              <Input
                id="email-subject"
                ref={subjectInputRef}
                placeholder="Sujet du mail"
                value={emailData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
              />
            </div>

            <div className="flex-grow">
              <Label>Contenu du mail</Label>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-2">
                  <TabsTrigger value="edit">Éditer</TabsTrigger>
                  <TabsTrigger value="preview" onClick={() => onPreview(emailData)}>Prévisualiser</TabsTrigger>
                </TabsList>
                <TabsContent value="edit" className="pt-4">
                  <Editor
                    onInit={(evt, editor) => editorRef.current = editor}
                    apiKey="autj5mdkjk2clugbltboounn51byfb9aia5vb7hyke03f2vc" 
                    init={{
                      height: 400,
                      menubar: true,
                      plugins: 'link image code table lists advlist',
                      toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | link image',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                    value={emailData.content}
                    onEditorChange={(content) => handleChange('content', content)}
                  />
                </TabsContent>
              </Tabs>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => window.history.back()} className="rounded-xl font-semibold border-slate-200 text-slate-600 hover:bg-slate-50">Annuler</Button>
              <Button onClick={handleSave} className="rounded-xl font-bold bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white shadow-lg shadow-blue-500/25 transition-all hover:-translate-y-0.5">Enregistrer</Button>
            </div>
          </div>
        </ResizablePanel>
        
        <ResizableHandle withHandle />
        
        <ResizablePanel defaultSize={34}>
          <div className="h-full p-4 border-l">
            <Card className="h-full rounded-2xl border-slate-200/80 bg-gradient-to-br from-white to-slate-50/60 shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-black text-slate-900">Variables disponibles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 overflow-y-auto" style={{ maxHeight: "calc(100% - 70px)" }}>
                <p className="text-xs text-gray-500 mb-4">
                  Cliquez sur une variable pour l'insérer à la position du curseur dans le sujet ou le contenu du mail.
                </p>
                {renderVariableButtons(apprenantVariables, "Apprenant")}
                
                {emailData.type === 'session' ? (
                  <>
                    {renderVariableButtons(formationVariables, "Formation")}
                    {renderVariableButtons(evenementVariables, "Événement")}
                  </>
                ) : (
                  renderVariableButtons(apprenantSpecificVariables, "Apprenant spécifique")
                )}
              </CardContent>
            </Card>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};

export default EmailEditor;
